import {
  addAddressAsync,
  buyVirtualOfficeAsync,
  changePasswordAsync,
  confirm2FAEmailAsync,
  confirm2FAVerificationAsync,
  confirmVerificationAsync,
  disableVerificationStatusAsync,
  enableVerificationStatusAsync,
  getAddressAsync,
  getLicensesAsync,
  getSettingsAccountAsync,
  getSettingsNotificationsAsync,
  getTeamSettingsAsync,
  getVerificationSecurityAsync,
  sendEmailCodeForTwoFaAsync,
  setNotificationAsync,
  updateTeamSettingsAsync,
  updateUsernameAsync,
  uploadProfileImageAsync
} from './settings.requests';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { Dispatch, SetStateAction } from 'react';
import { RootState } from '../../app/store';
import { ErrorToast, SuccessToast } from '../../components';
import { setUserToLS } from '../../helpers/localstorage-helper';
import {
  changeAvailableRewardPoints,
  changeAvailableShoppingPoints,
  changeUserInfo,
  getCurrentLicense,
  updateVirtualOfficeLicence
} from '../../slices/auth-slice';
import { getWallets } from '../../slices/wallets-slice';
import { NetworkType } from '../../types/types';

export const settingsInit = createAsyncThunk(
  'settingsInit',
  async (_, { dispatch }) => {
    const { data: user } = await getSettingsAccountAsync();
    const { data: notifications } = await getSettingsNotificationsAsync();
    const { data: teamSettings } = await getTeamSettingsAsync();
    const {
      data: is2FAEnabled,
      data: signIn2FAEnabled,
      data: signInEmailCodeVerificationEnabled
    } = await getVerificationSecurityAsync();

    interface NotificationFormatted {
      id: number;
      checked: boolean;
    }

    const newNotifications: NotificationFormatted[] = [
      {
        id: 1,
        checked: notifications!.notificationSettings.earnings
      },
      {
        id: 2,
        checked: notifications!.notificationSettings.news
      },
      {
        id: 3,
        checked: notifications!.notificationSettings.promotions
      },
      {
        id: 4,
        checked: notifications!.notificationSettings.referral
      }
    ];

    dispatch(getAddress());

    dispatch(getWallets());

    return {
      user: user?.user,
      notifications: newNotifications,
      teamSettings: teamSettings?.settings.teamCompletionSorting,
      verificationStatus: is2FAEnabled?.is2FAEnabled,
      signIn2FAEnabled: signIn2FAEnabled?.signIn2FAEnabled,
      signInEmailCodeVerificationEnabled:
        signInEmailCodeVerificationEnabled?.signInEmailCodeVerificationEnabled
    };
  }
);

export const confirmVerification = createAsyncThunk(
  'confirmVerification',
  async ({
    emailCode,
    twoFACode,
    setStep
  }: {
    emailCode: string;
    twoFACode: string;
    setStep: Dispatch<SetStateAction<number>>;
  }) => {
    try {
      const { data } = await confirmVerificationAsync(emailCode, twoFACode);
      setStep(3);
      return data!.backupKey;
    } catch (error: any) {
      ErrorToast(error.response.data.error);
    }
  }
);

export const confirm2FAVerification = createAsyncThunk(
  'confirm2FAVerification',
  async ({
    emailCode,
    twoFACode,
    signIn2FAEnabled,
    setIsOpen
  }: {
    emailCode: string;
    twoFACode: string;
    signIn2FAEnabled: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
  }) => {
    try {
      const { data } = await confirm2FAVerificationAsync(
        emailCode,
        twoFACode,
        signIn2FAEnabled
      );
      setIsOpen(false);
      return data!.signIn2FAEnabled;
    } catch (error: any) {
      ErrorToast(error.response.data.error);
    }
  }
);

export const updateUsername = createAsyncThunk(
  'updateUsername',
  async ({
    username,
    setIsOpen
  }: {
    username: string;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
  }) => {
    try {
      const { data } = await updateUsernameAsync(username);
      setIsOpen(false);
      return data!.user;
    } catch (error: any) {
      ErrorToast(error.response.data.error);
    }
  }
);

export const changePassword = createAsyncThunk(
  'changePassword',
  async ({
    oldPassword,
    newPassword,
    setStep
  }: {
    oldPassword: string;
    newPassword: string;
    setStep: Dispatch<SetStateAction<number>>;
  }) => {
    try {
      const { message } = await changePasswordAsync(oldPassword, newPassword);
      SuccessToast(message);
      setStep(2);
    } catch (error: any) {
      ErrorToast(error.response.data.error);
    }
  }
);

export const updateTeamSettings = createAsyncThunk(
  'updateTeamSettings',
  async (id: number) => {
    const { data } = await updateTeamSettingsAsync(id);
    SuccessToast();
    return data!.settings.teamCompletionSorting;
  }
);

export const setNotification = createAsyncThunk(
  'setNotification',
  async (item: any) => {
    let notifications;
    switch (item.id) {
      case 1:
        notifications = {
          earnings: !item.checked
        };
        break;
      case 2:
        notifications = {
          news: !item.checked
        };
        break;

      case 3:
        notifications = {
          promotions: !item.checked
        };
        break;

      case 4:
        notifications = {
          referral: !item.checked
        };
        break;
      default:
        break;
    }

    await setNotificationAsync(notifications);

    return item;
  }
);

export const enableVerificationStatus = createAsyncThunk(
  'enableVerificationStatus',
  async () => {
    const { data } = await enableVerificationStatusAsync();
    return data!.user2FAData;
  }
);

export const confirm2FAEmail = createAsyncThunk(
  'confirm2FAEmail',
  async (
    {
      code,
      setIsOpen
    }: {
      code: string;
      setIsOpen: Dispatch<SetStateAction<boolean>>;
    },
    { rejectWithValue }
  ) => {
    try {
      const { message } = await confirm2FAEmailAsync(code);
      setIsOpen(false);
      SuccessToast(message);
      return true;
    } catch (error: any) {
      ErrorToast(error.response.data.error);
      rejectWithValue(false);
    }
  }
);

export const sendEmailCodeForTwoFa = createAsyncThunk(
  'sendEmailCodeForTwoFa',
  async (
    {
      type
    }: {
      type: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await sendEmailCodeForTwoFaAsync(type);
      SuccessToast(data.message);
      return true;
    } catch (error: any) {
      ErrorToast(error.response.data.error);
      rejectWithValue(false);
    }
  }
);

export const disableVerificationStatus = createAsyncThunk(
  'disableVerificationStatus',
  async (
    {
      code,
      twoFACode,
      setIsOpen
    }: {
      code: string;
      twoFACode: string;
      setIsOpen: Dispatch<SetStateAction<boolean>>;
    },
    { rejectWithValue }
  ) => {
    try {
      const { message } = await disableVerificationStatusAsync(code, twoFACode);

      setIsOpen(false);

      SuccessToast(message);

      console.log('3');
    } catch (error: any) {
      console.log('error');

      ErrorToast(error.response.data.error);
      rejectWithValue(false);
    }
  }
);

export const buyVirtualOffice = createAsyncThunk(
  'buyVirtualOffice',
  async (
    {
      licenceId,
      networkType,
      currencyType,
      step,
      setStep
    }: {
      licenceId: number;
      networkType: NetworkType | null;
      currencyType: number;
      step: number;
      setStep: Dispatch<SetStateAction<number>>;
    },
    { dispatch, getState }
  ) => {
    try {
      const { data } = await buyVirtualOfficeAsync(
        licenceId,
        networkType,
        currencyType
      );
      setStep(step);

      const state = getState() as RootState;

      setStep(step);

      if (currencyType === 3) {
        const user = {
          ...state.auth.userInfo,
          user: {
            ...state.auth.userInfo.user,
            userTickets: {
              ...state.auth.userInfo.user.userTickets,
              availableRewardPoints:
                state.auth.userInfo.user.userTickets.availableRewardPoints -
                data!.licence.price
            }
          }
        };

        setUserToLS(user);

        dispatch(
          changeAvailableRewardPoints(
            state.auth.userInfo.user.userTickets.availableRewardPoints -
              data!.licence.price
          )
        );
      } else if (currencyType === 2) {
        const user = {
          ...state.auth.userInfo,
          user: {
            ...state.auth.userInfo.user,
            userTickets: {
              ...state.auth.userInfo.user.userTickets,
              shoppingPoints:
                state.auth.userInfo.user.userTickets.shoppingPoints -
                data!.licence.price
            }
          }
        };

        setUserToLS(user);

        dispatch(
          changeAvailableShoppingPoints(
            state.auth.userInfo.user.userTickets.shoppingPoints -
              data!.licence.price
          )
        );
      }

      // dispatch(updateVirtualOfficeLicence(data!.licence));

      dispatch(getCurrentLicense());

      // const user = {
      //   ...state.auth.userInfo,
      //   user: {
      //     ...state.auth.userInfo.user,
      //     currentVirtualOfficeLicence: data!.licence
      //   }
      // };

      // setUserToLS(user);

      return data!.licence;
    } catch (error: any) {
      ErrorToast(error.response.data.error);
    }
  }
);

export const uploadProfileImage = createAsyncThunk(
  'uploadProfileImage',
  async (formData: FormData, { getState, dispatch }) => {
    const { auth } = getState() as RootState;
    const { userInfo } = auth;

    const { data } = await uploadProfileImageAsync(formData);

    const user = {
      ...userInfo,
      user: {
        ...userInfo.user,
        profileImageUrl: data?.profileImageUrl || ''
      }
    };

    setUserToLS(user);
    dispatch(changeUserInfo(user));
    SuccessToast('Successfully changed profile image');

    return data?.profileImageUrl;
  }
);

export const addAddress = createAsyncThunk(
  'addAddress',
  async ({
    addressLine1,
    addressLine2,
    zipCode,
    country,
    countryCode,
    isPermanent,
    city,
    setIsOpen2
  }: {
    addressLine1: string;
    addressLine2: string;
    zipCode: string;
    country: string;
    countryCode: string;
    isPermanent: boolean;
    city: string;
    setIsOpen2: Dispatch<SetStateAction<boolean>>;
  }) => {
    const { data, message } = await addAddressAsync({
      addressLine1,
      addressLine2,
      zipCode,
      country,
      countryCode: countryCode ? countryCode : '+386',
      isPermanent,
      city
    });

    SuccessToast(message);

    setIsOpen2(false);

    return data;
  }
);

export const getAddress = createAsyncThunk('getAddress', async () => {
  const { data } = await getAddressAsync();
  return data!.addresses;
});

export const getLicenses = createAsyncThunk('getLicenses', async () => {
  const { data } = await getLicensesAsync();

  // const saveBuying = (product: any) => {
  //   if (product?.name === '5 years subscription') {
  //     return 39;
  //   } else if (product?.name === '3 years subscription') {
  //     return 32;
  //   }
  // };

  // const accessYears = (product: any) => {
  //   if (product?.name === '5 years subscription') {
  //     return 5;
  //   } else if (product?.name === '3 years subscription') {
  //     return 3;
  //   } else {
  //     return 1;
  //   }
  // };

  const newProducts = data!.products?.map((product: any) => {
    return {
      ...product
    };
  });

  return newProducts;
});
